import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FormControl, Select, MenuItem, Button, TextField, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
// import { DigitalClock } from '@mui/x-date-pickers/DigitalClock';
import dayjs from 'dayjs';
import styles from './styles.module.css';
import { useTranslation } from 'react-i18next';
import DateLimitDialog from '../../popups/DateLimitDialog';

const DAYS = {
  TODAY: 'today',
  YESTERDAY: 'yesterday',
  LAST_3_DAYS: 'last3days',
  LAST_7_DAYS: 'last7days',
  THIS_MONTH: 'thisMonth',
  LAST_MONTH: 'lastMonth',
  CUSTOM: 'custom',
};

const DateTimeRange = ({ setFilterFromDate, setFilterToDate, storageKey, showTimeSelector = true }) => {
  const { t } = useTranslation('common');

  const [selectedOption, setSelectedOption] = useState(DAYS.TODAY);
  const [fromDate, setFromDate] = useState(dayjs().subtract(1, 'day').startOf('day'));
  const [fromTime, setFromTime] = useState(dayjs().startOf('day'));
  const [toDate, setToDate] = useState(dayjs().subtract(1, 'day').endOf('day'));
  const [toTime, setToTime] = useState(dayjs().endOf('day'));
  const [showDateLimitWarning, setShowDateLimitWarning] = useState(false);
  const selectBoxRef = useRef(null);

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [previousOption, setPreviousOption] = useState(DAYS.TODAY);

  const handleDateRangeClick = useCallback(
    (range) => {
      const today = dayjs().startOf('day');
      let start, end;

      switch (range) {
        case DAYS.TODAY:
          start = today;
          end = today.endOf('day');
          break;
        case DAYS.YESTERDAY:
          start = today.subtract(1, 'day');
          end = today.subtract(1, 'day').endOf('day');
          break;
        case DAYS.LAST_3_DAYS:
          start = today.subtract(2, 'day');
          end = today.endOf('day');
          break;
        case DAYS.LAST_7_DAYS:
          start = today.subtract(6, 'day');
          end = today.endOf('day');
          break;
        case DAYS.THIS_MONTH:
          start = dayjs().startOf('month');
          end = dayjs().endOf('day');
          break;
        case DAYS.LAST_MONTH:
          start = dayjs().subtract(1, 'month').startOf('month');
          end = dayjs().subtract(1, 'month').endOf('month');
          break;
        default:
          return;
      }

      setFromDate(start);
      setFromTime(start.startOf('day'));
      setToDate(end);
      setToTime(end.endOf('day'));

      setFilterFromDate(start.toDate());
      setFilterToDate(end.toDate());
      localStorage.setItem(storageKey, JSON.stringify({ range }));
    },
    [setFilterFromDate, setFilterToDate, storageKey]
  );

  useEffect(() => {
    const cached = localStorage.getItem(storageKey);
    if (cached) {
      const parsed = JSON.parse(cached);
      const range = parsed.range || DAYS.TODAY;
      setSelectedOption(range);
      if (range !== DAYS.CUSTOM) {
        setPreviousOption(range);
      }
      if (range === DAYS.CUSTOM && parsed.fromDate && parsed.toDate) {
        const start = dayjs(parsed.fromDate);
        const end = dayjs(parsed.toDate);
        setFromDate(start);
        setFromTime(start);
        setToDate(end);
        setToTime(end);
        setFilterFromDate(start.toDate());
        setFilterToDate(end.toDate());
      } else {
        handleDateRangeClick(range);
      }
    } else {
      setSelectedOption(DAYS.TODAY);
      setPreviousOption(DAYS.TODAY);
      handleDateRangeClick(DAYS.TODAY);
    }
  }, [handleDateRangeClick, storageKey, setFilterFromDate, setFilterToDate]);

  const setDefaultCustomDates = useCallback(() => {
    const today = dayjs().startOf('day');
    const start = today.subtract(1, 'day');
    const end = today.endOf('day');
    setFromDate(start);
    setFromTime(start.startOf('day'));
    setToDate(end);
    setToTime(end.endOf('day'));
  }, []);

  const handleChange = (event) => {
    const value = event.target.value;
    if (value === DAYS.CUSTOM) {
      setPreviousOption(selectedOption);
      setSelectedOption(DAYS.CUSTOM);

      const cached = localStorage.getItem(storageKey);
      if (cached) {
        const parsed = JSON.parse(cached);
        if (parsed.range === DAYS.CUSTOM && parsed.fromDate && parsed.toDate) {
          const start = dayjs(parsed.fromDate);
          const end = dayjs(parsed.toDate);
          setFromDate(start);
          setFromTime(start);
          setToDate(end);
          setToTime(end);
        } else {
          setDefaultCustomDates();
        }
      } else {
        setDefaultCustomDates();
      }

      setIsDialogOpen(true);
    } else {
      setSelectedOption(value);
      setPreviousOption(value);
      handleDateRangeClick(value);
    }
  };

  const handleCancelCustomDate = () => {
    setSelectedOption(previousOption);
    setIsDialogOpen(false);
  };

  const handleApplyCustomDate = () => {
    if (fromDate && toDate) {
      const startHour = fromTime ? fromTime.hour() : 0;
      const startMin = fromTime ? fromTime.minute() : 0;
      const endHour = toTime ? toTime.hour() : 23;
      const endMin = toTime ? toTime.minute() : 59;

      const fromDateTime = fromDate.set('hour', startHour).set('minute', startMin).set('second', 0).set('millisecond', 0);
      const toDateTime = toDate.set('hour', endHour).set('minute', endMin).set('second', 59).set('millisecond', 999);

      const limitDate = dayjs('2024-04-01').startOf('day');
      if (fromDateTime.isBefore(limitDate)) {
        setShowDateLimitWarning(true);
        return;
      }

      setFilterFromDate(fromDateTime.toDate());
      setFilterToDate(toDateTime.toDate());

      localStorage.setItem(
        storageKey,
        JSON.stringify({
          range: DAYS.CUSTOM,
          fromDate: fromDateTime.format('YYYY-MM-DD HH:mm'),
          toDate: toDateTime.format('YYYY-MM-DD HH:mm'),
        })
      );
      setIsDialogOpen(false);
    } else {
      alert('Please select both dates.');
    }
  };

  return (
    <div>
      <div style={{ paddingBottom: '4px' }}>
        <label className={styles.label}>{t('Date Range')}</label>
      </div>
      <div style={{ height: '40px' }} ref={selectBoxRef}>
        <FormControl fullWidth variant="outlined" size="small">
          <Select
            size="small"
            value={selectedOption}
            onChange={handleChange}
            style={{ display: 'flex', alignItems: 'center', paddingTop: '4px' }}
            className={styles.select}
            renderValue={(value) => {
              if (value === DAYS.CUSTOM && fromDate && toDate) {
                const formattedFromDate = fromDate.format('DD/MM/YYYY');
                const formattedToDate = toDate.format('DD/MM/YYYY');
                if (showTimeSelector && fromTime && toTime) {
                  const formattedFromTime = fromTime.format('hh:mm A');
                  const formattedToTime = toTime.format('hh:mm A');
                  return `${formattedFromDate} ${formattedFromTime} - ${formattedToDate} ${formattedToTime}`;
                }
                return `${formattedFromDate} - ${formattedToDate}`;
              }
              switch (value) {
                case DAYS.TODAY:
                  return t('Today');
                case DAYS.YESTERDAY:
                  return t('Yesterday');
                case DAYS.LAST_3_DAYS:
                  return t('Last 3 Days');
                case DAYS.LAST_7_DAYS:
                  return t('Last 7 Days');
                case DAYS.THIS_MONTH:
                  return t('This Month');
                case DAYS.LAST_MONTH:
                  return t('Last Month');
                default:
                  return t('Custom');
              }
            }}
            MenuProps={{
              MenuListProps: {
                sx: {
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  width: '100%',
                },
              },
            }}
          >
            <MenuItem value={DAYS.TODAY} className={styles.menuItems}>
              {t('Today')}
            </MenuItem>
            <MenuItem value={DAYS.YESTERDAY} className={styles.menuItems}>
              {t('Yesterday')}
            </MenuItem>
            <MenuItem value={DAYS.LAST_3_DAYS} className={styles.menuItems}>
              {t('Last 3 Days')}
            </MenuItem>
            <MenuItem value={DAYS.LAST_7_DAYS} className={styles.menuItems}>
              {t('Last 7 Days')}
            </MenuItem>
            <MenuItem value={DAYS.THIS_MONTH} className={styles.menuItems}>
              {t('This Month')}
            </MenuItem>
            <MenuItem value={DAYS.LAST_MONTH} className={styles.menuItems}>
              {t('Last Month')}
            </MenuItem>

            <MenuItem
              className={styles.menuItems}
              value={DAYS.CUSTOM}
              onClick={() => {
                if (selectedOption === DAYS.CUSTOM) {
                  setIsDialogOpen(true);
                }
              }}
            >
              {t('Custom')}
            </MenuItem>
          </Select>
        </FormControl>
      </div>

      <Dialog 
        open={isDialogOpen} 
        onClose={handleCancelCustomDate}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '10px',
            padding: '4px'
          }
        }}
      >
        <DialogTitle style={{ fontSize: '16px', fontWeight: 'bold', paddingBottom: '8px' }}>
          {t('Custom')}
        </DialogTitle>
        <DialogContent>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <div className={styles.dialogCustomDateContainer}>
              <div className={styles.inlineRow}>
                <div className={styles.inlineLabel}><strong>{t('from')}</strong></div>
                <div className={styles.inlinePickerGroup}>
                  <DatePicker
                    label={t('Date')}
                    value={fromDate}
                    onChange={(v) => setFromDate(v)}
                    maxDate={dayjs()}
                    sx={{ width: '100%' }}
                    renderInput={(params) => <TextField size="small" {...params} />}
                    format="DD/MM/YYYY"
                  />
                  {showTimeSelector && (
                    <TimePicker
                      label={t('Time')}
                      value={fromTime}
                      onChange={(v) => setFromTime(v)}
                      sx={{ width: '100%' }}
                      renderInput={(params) => <TextField size="small" {...params} />}
                      ampm
                    />
                  )}
                </div>
              </div>

              <div className={styles.inlineRow}>
                <div className={styles.inlineLabel}><strong>{t('to')}</strong></div>
                <div className={styles.inlinePickerGroup}>
                  <DatePicker
                    label={t('Date')}
                    value={toDate}
                    onChange={(v) => setToDate(v)}
                    maxDate={dayjs()}
                    sx={{ width: '100%' }}
                    renderInput={(params) => <TextField size="small" {...params} />}
                    format="DD/MM/YYYY"
                  />
                  {showTimeSelector && (
                    <TimePicker
                      label={t('Time')}
                      value={toTime}
                      onChange={(v) => setToTime(v)}
                      sx={{ width: '100%' }}
                      renderInput={(params) => <TextField size="small" {...params} />}
                      ampm
                    />
                  )}
                </div>
              </div>
            </div>
          </LocalizationProvider>
        </DialogContent>
        <DialogActions style={{ padding: '16px 24px' }}>
          <Button 
            onClick={handleCancelCustomDate} 
            color="secondary"
            style={{ textTransform: 'none' }}
          >
            {t('Cancel')}
          </Button>
          <Button
            onClick={handleApplyCustomDate}
            variant="contained"
            color="primary"
            style={{ backgroundColor: '#00a65a', color: '#fff', textTransform: 'none' }}
          >
            {t('Apply')}
          </Button>
        </DialogActions>
      </Dialog>

      {showDateLimitWarning && (
        <DateLimitDialog
          handleClose={() => setShowDateLimitWarning(false)}
          title={t('Date Limit Warning')}
          message={t('date_limit_warning_message')}
        />
      )}
    </div>
  );
};

export default DateTimeRange;
